import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Heart, LogOut, Menu, Shield, ShoppingBasket, Smartphone, User, X, Zap } from "lucide-react";
import { api } from "@/lib/api";
import { useStore } from "@/context/StoreContext";

const LINKS = [
  { to: "/", label: "HOME", end: true },
  { to: "/store/keys", label: "KEYS" },
  { to: "/store/ranks", label: "RANKS" },
  { to: "/store/ulti", label: "+ ULTI+" },
  { to: "/store/donate", label: "DONATE", icon: Heart },
];

const McHead = ({ ign, className = "w-4 h-4" }) => (
  <img
    src={`https://mc-heads.net/avatar/${encodeURIComponent(ign)}/32`}
    alt=""
    loading="lazy"
    onError={(e) => { e.currentTarget.style.display = "none"; }}
    className={`${className} rounded-[3px] [image-rendering:pixelated]`}
  />
);

export const Navbar = () => {
  const { count, setBasketOpen, user, logoutUser, username, platform } = useStore();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [flash, setFlash] = useState(null);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem("ulti_flash_dismissed") || "");
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    api.get("/flash-sale").then((r) => setFlash(r.data)).catch(() => {});
  }, [location.pathname]);

  const flashActive = !!flash?.active;
  const showBanner = flashActive && dismissed !== flash.ends_at;

  useEffect(() => {
    if (!showBanner) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [showBanner]);

  const dismissBanner = () => {
    localStorage.setItem("ulti_flash_dismissed", flash.ends_at);
    setDismissed(flash.ends_at);
  };

  let countdownText = "";
  if (showBanner) {
    const diff = Math.max(0, new Date(flash.ends_at).getTime() - now);
    const d = Math.floor(diff / 86400000);
    const h = Math.floor((diff % 86400000) / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    const s = Math.floor((diff % 60000) / 1000);
    countdownText = d > 0
      ? `${d}d ${String(h).padStart(2, "0")}h ${String(m).padStart(2, "0")}m`
      : `${String(h).padStart(2, "0")}h ${String(m).padStart(2, "0")}m ${String(s).padStart(2, "0")}s`;
  }

  if (location.pathname.startsWith("/admin")) return null;

  const linkClass = ({ isActive }) =>
    `px-4 py-2 rounded-lg text-xs font-bold tracking-[0.15em] transition-[background-color,color] duration-300 ${
      isActive ? "bg-white/10 text-white" : "text-slate-400 hover:text-white hover:bg-white/5"
    }`;

  return (
    <div className="fixed top-0 inset-x-0 z-50">
      {showBanner && (
        <div data-testid="flash-banner" className="relative bg-gradient-to-r from-amber-500 via-amber-400 to-amber-500 text-black">
          <div className="max-w-7xl mx-auto px-4 h-10 flex items-center justify-center gap-3 text-[11px] sm:text-xs font-bold tracking-[0.1em]">
            <Zap className="w-3.5 h-3.5 shrink-0" />
            <span className="hidden sm:inline">{flash.title}</span>
            <span>
              ENDS IN <span className="tabular-nums" data-testid="flash-banner-countdown">{countdownText}</span>
            </span>
            <Link to="/flash-sale" data-testid="flash-banner-link" className="underline underline-offset-2 hover:opacity-75 transition-opacity duration-200">
              VIEW SALE
            </Link>
          </div>
          <button
            onClick={dismissBanner}
            data-testid="flash-banner-dismiss"
            aria-label="Dismiss flash sale banner"
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-black/10 transition-colors duration-200"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      <header data-testid="main-navbar" className="bg-[#0D0B12]/70 backdrop-blur-xl border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
        <Link to="/" data-testid="nav-logo-link" className="flex items-center gap-2 shrink-0">
          <Shield className="w-5 h-5 text-[#8B5CF6]" strokeWidth={2.5} />
          <span className="font-display text-lg tracking-tight text-white">
            ULTI<span className="text-[#A78BFA]">SMP</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1" data-testid="nav-links">
          {LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} className={linkClass} data-testid={`nav-link-${l.label.replace(/[^A-Z]/gi, "").toLowerCase()}`}>
              <span className="inline-flex items-center gap-1.5">
                {l.icon && <l.icon className="w-3 h-3 fill-current" />}
                {l.label}
              </span>
            </NavLink>
          ))}
          <NavLink to="/flash-sale" className={linkClass} data-testid="nav-link-sale">
            <span className="inline-flex items-center gap-1.5">
              <Zap className="w-3 h-3 text-amber-400" />
              SALE
              {flashActive && (
                <span className="relative flex h-2 w-2" data-testid="nav-flash-live-badge">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-400" />
                </span>
              )}
            </span>
          </NavLink>
          <a
            href="https://discord.gg/kMUdQmtcfj"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-discord-link"
            className="ml-2 px-4 py-2 rounded-lg text-xs font-bold tracking-[0.15em] bg-[#5865F2]/90 hover:bg-[#5865F2] text-white transition-[background-color] duration-300"
          >
            DISCORD
          </a>
        </nav>

        <div className="flex items-center gap-3">
          {user ? (
            <div className="hidden sm:flex items-center gap-2" data-testid="nav-user-chip">
              <span className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-xs font-semibold text-slate-300">
                {user.ign ? <McHead ign={user.ign} /> : <User className="w-3.5 h-3.5 text-[#8B5CF6]" />}
                {user.ign || user.email}
                {user.role !== "member" && (
                  <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold tracking-widest ${user.role === "staff" ? "bg-emerald-400/20 text-emerald-300" : "bg-amber-400/20 text-amber-300"}`} data-testid="nav-user-role">
                    {user.role.toUpperCase()}
                  </span>
                )}
              </span>
              <button onClick={logoutUser} data-testid="nav-logout-button" aria-label="Log out" className="p-2 rounded-lg text-slate-500 hover:text-red-400 hover:bg-red-400/10 transition-colors duration-200">
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => navigate("/login")}
              data-testid="nav-account-button"
              className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold tracking-[0.15em] text-slate-400 hover:text-white hover:bg-white/5 transition-colors duration-300"
            >
              {username ? (
                platform === "bedrock" ? <Smartphone className="w-4 h-4 text-sky-400" /> : <McHead ign={username} />
              ) : <User className="w-4 h-4" />} {username ? username.toUpperCase() : "ACCOUNT"}
            </button>
          )}
          <button
            onClick={() => setBasketOpen(true)}
            data-testid="nav-basket-button"
            className="relative flex items-center gap-2 px-4 py-2 rounded-lg bg-[#8B5CF6] hover:bg-[#7C3AED] text-white text-xs font-bold tracking-[0.15em] transition-[background-color,transform] duration-300 hover:-translate-y-0.5"
          >
            <ShoppingBasket className="w-4 h-4" />
            BASKET
            {count > 0 && (
              <span data-testid="nav-basket-count" className="absolute -top-2 -right-2 min-w-5 h-5 px-1 rounded-full bg-white text-[#7C3AED] text-[10px] font-extrabold flex items-center justify-center">
                {count}
              </span>
            )}
          </button>
          <button
            className="lg:hidden text-slate-300 hover:text-white"
            onClick={() => setMobileOpen((o) => !o)}
            data-testid="nav-mobile-menu-button"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <nav className="lg:hidden border-t border-white/10 bg-[#0D0B12]/95 backdrop-blur-xl px-4 py-3 flex flex-col gap-1" data-testid="nav-mobile-menu">
          {LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} onClick={() => setMobileOpen(false)} className={linkClass}>
              {l.label}
            </NavLink>
          ))}
          <NavLink to="/flash-sale" onClick={() => setMobileOpen(false)} className={linkClass}>
            SALE{flashActive ? " • LIVE" : ""}
          </NavLink>
          {user ? (
            <button onClick={() => { logoutUser(); setMobileOpen(false); }} className="px-4 py-2 text-left text-xs font-bold tracking-[0.15em] text-slate-400 hover:text-white" data-testid="nav-mobile-logout">
              LOGOUT ({user.ign || user.email})
            </button>
          ) : (
            <button onClick={() => { navigate("/login"); setMobileOpen(false); }} className="px-4 py-2 text-left text-xs font-bold tracking-[0.15em] text-slate-400 hover:text-white flex items-center gap-2" data-testid="nav-mobile-account">
              {username && (platform === "bedrock" ? <Smartphone className="w-4 h-4 text-sky-400" /> : <McHead ign={username} />)}
              {username ? username.toUpperCase() : "ACCOUNT"}
            </button>
          )}
          <a href="https://discord.gg/kMUdQmtcfj" target="_blank" rel="noopener noreferrer" className="px-4 py-2 text-xs font-bold tracking-[0.15em] text-[#8B9BF4]">
            DISCORD
          </a>
        </nav>
      )}
      </header>
    </div>
  );
};
